import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface AiElementContext {
  elementId: string;
  fileName: string;
  projectId: string;
  outputId: string;
  secretId: string;
}

export type AiAgentId = 'transcript' | 'global' | 'page-help';
export type AiPreset = 'explain-bill' | 'action-items' | 'find-receipts';
export const AI_PRESETS: Record<AiPreset, { label: string; question: string }> = {
  'explain-bill': { label: 'Explain bill', question: 'Explain this bill/invoice in simple terms — merchant, total, due date, and what each line item is.' },
  'action-items': { label: 'Action items', question: 'List all action items, due dates, and next steps from this document.' },
  'find-receipts': { label: 'Find receipts', question: 'Find all receipts/invoices and list merchant, date, and total for each.' },
};

export const useAiToolsStore = defineStore('aiTools', () => {
  const showSummarize = ref(false);
  const showAsk = ref(false);
  const selectedElement = ref<AiElementContext | null>(null);
  const askScope = ref<'single' | 'global'>('global');
  const askInitialQuestion = ref('');
  const activeAgent = ref<AiAgentId>('global');

  function openSummarize(ctx?: AiElementContext | null) {
    selectedElement.value = ctx ?? null;
    // Summarize is always transcript-scoped when an element is present
    activeAgent.value = ctx ? 'transcript' : 'global';
    showSummarize.value = true;
  }

  function openAsk(opts: { scope?: 'single' | 'global'; agent?: AiAgentId; question?: string; element?: AiElementContext | null } = {}) {
    if (opts.element !== undefined) selectedElement.value = opts.element;
    if (opts.scope) askScope.value = opts.scope;
    else if (!selectedElement.value) askScope.value = 'global';
    if (opts.agent) activeAgent.value = opts.agent;
    else activeAgent.value = askScope.value === 'single' ? 'transcript' : 'global';
    askInitialQuestion.value = opts.question ?? '';
    showAsk.value = true;
  }

  function closeSummarize() { showSummarize.value = false; }
  function closeAsk() { showAsk.value = false; askInitialQuestion.value = ''; }

  /** Clear stale transcript reference when leaving transcript pages */
  function clearStaleTranscriptContext() {
    // Keep the element for global scope but mark agent as global so UI doesn't
    // imply "this file" when the user is on Orders/Team/etc.
    if (askScope.value === 'single') askScope.value = 'global';
    activeAgent.value = 'global';
  }

  return {
    showSummarize, showAsk, selectedElement, askScope, askInitialQuestion, activeAgent,
    openSummarize, openAsk, closeSummarize, closeAsk, clearStaleTranscriptContext,
  };
});
